import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  MessageCircle,
  LayoutGrid,
  MapPin,
  Search,
  ListChecks,
  ArrowUpRight,
  ArrowRight,
} from "lucide-react";
import { Seo } from "../components/Seo";
import { Reveal } from "../components/Reveal";
import {
  site,
  categories,
  faq,
  waLink,
  waGeneralMessage,
  waInfoMessage,
} from "../data/store";

const INFO_LABELS = {
  satuanGrosir: "Pembelian satuan/grosir",
  pengantaran: "Layanan pengantaran",
  wilayah: "Wilayah pengantaran",
  ongkir: "Ketentuan ongkir",
  pembayaran: "Metode pembayaran",
  penawaranProyek: "Penawaran kebutuhan proyek",
};

const easeOut = [0.16, 1, 0.3, 1];

export default function Home() {
  return (
    <>
      <Seo
        title="Nuansa Jaya Abadi (NJA) | Toko Bangunan Bajawa"
        description="Toko Bangunan Nuansa Jaya Abadi (NJA) di Bajawa, Kabupaten Ngada. Bahan bangunan, cat, keramik, pipa, listrik, dan perkakas. Tanya harga via WhatsApp."
        path="/"
      />

      {/* Hero */}
      <section className="bg-navy text-white">
        <div
          className={`mx-auto max-w-[1200px] px-4 py-12 sm:px-6 lg:py-20 ${
            site.storePhotoUrl ? "grid items-center gap-10 lg:grid-cols-2" : ""
          }`}
        >
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: easeOut }}
            className={site.storePhotoUrl ? "" : "max-w-3xl"}
          >
            <p className="inline-block rounded-full border border-white/25 px-4 py-1.5 text-sm font-medium text-white/85">
              Toko bangunan di Bajawa
            </p>
            <h1 className="mt-5 text-[32px] font-extrabold leading-tight sm:text-[44px] lg:text-[52px]">
              Bangun & renovasi bersama Nuansa Jaya Abadi
            </h1>
            <span className="mt-6 block h-1.5 w-16 rounded-full bg-brand" aria-hidden="true" />
            <p className="mt-6 max-w-2xl text-base leading-relaxed text-white/85 sm:text-lg">
              Temukan kebutuhan bahan bangunan dan perlengkapan rumah. Tanyakan
              harga serta ketersediaan barang langsung kepada tim NJA.
            </p>
            <div className="mt-8 flex flex-col gap-3 sm:flex-row">
              <a
                href={waLink(waGeneralMessage)}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="hero-whatsapp-button"
                className="inline-flex items-center justify-center gap-2 rounded-full bg-wa px-7 py-3.5 text-base font-semibold text-white transition-colors duration-300 hover:bg-wa-dark"
              >
                <MessageCircle className="h-5 w-5" aria-hidden="true" />
                Tanya Harga via WhatsApp
              </a>
              <Link
                to="/produk"
                data-testid="hero-catalog-button"
                className="inline-flex items-center justify-center gap-2 rounded-full border border-white/30 px-7 py-3.5 text-base font-semibold text-white transition-colors duration-300 hover:bg-white/10"
              >
                <LayoutGrid className="h-5 w-5" aria-hidden="true" />
                Lihat Kategori Barang
              </Link>
            </div>
          </motion.div>

          {site.storePhotoUrl && (
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, ease: easeOut, delay: 0.2 }}
            >
              <img
                src={site.storePhotoUrl}
                srcSet={site.storePhotoSrcSet}
                sizes="(min-width: 1024px) 560px, 100vw"
                alt={site.storePhotoAlt}
                width="1080"
                height="1028"
                fetchPriority="high"
                data-testid="hero-store-photo"
                className="w-full rounded-2xl object-cover shadow-xl"
              />
            </motion.div>
          )}
        </div>
      </section>

      {/* Kategori */}
      <section className="mx-auto max-w-[1200px] px-4 py-10 sm:px-6 lg:py-16" aria-labelledby="kategori-heading">
        <Reveal>
          <p className="text-sm font-semibold text-brand">Katalog</p>
          <h2 id="kategori-heading" className="mt-2 text-2xl font-bold text-navy sm:text-4xl">
            Kategori barang
          </h2>
          <p className="mt-3 max-w-2xl text-base leading-relaxed text-slate-600">
            Jenis bahan bangunan yang tersedia di toko kami di Bajawa. Pilih
            kategori untuk melihat panduan singkat dan menanyakan ketersediaan.
          </p>
        </Reveal>

        <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {categories.map((c, i) => (
            <Reveal key={c.slug} delay={Math.min(i * 0.05, 0.35)}>
              <Link
                to={`/produk/${c.slug}`}
                data-testid={`category-card-${c.slug}`}
                className="group flex h-full flex-col rounded-2xl border border-slate-200 bg-white p-6 shadow-sm transition-shadow duration-300 hover:shadow-md"
              >
                <span className="inline-flex h-11 w-11 items-center justify-center rounded-lg bg-brand/10 text-brand transition-transform duration-300 group-hover:scale-110">
                  <c.icon className="h-6 w-6" aria-hidden="true" />
                </span>
                <span className="mt-4 block text-lg font-semibold text-navy">
                  {c.name}
                </span>
                <span className="mt-1 block flex-1 text-sm leading-relaxed text-slate-600">
                  {c.shortDesc}
                </span>
                <span className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-brand">
                  Lihat kategori
                  <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5" aria-hidden="true" />
                </span>
              </Link>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Alur */}
      <section className="bg-slate-50" aria-labelledby="alur-heading">
        <div className="mx-auto max-w-[1200px] px-4 py-10 sm:px-6 lg:py-16">
          <Reveal>
            <p className="text-sm font-semibold text-brand">Alur</p>
            <h2 id="alur-heading" className="mt-2 text-2xl font-bold text-navy sm:text-4xl">
              Cara menanyakan kebutuhan
            </h2>
          </Reveal>
          <div className="mt-8 grid gap-4 md:grid-cols-3">
            {[
              {
                icon: Search,
                num: "01",
                title: "Pilih kebutuhan",
                text: "Tentukan jenis barang, ukuran, dan perkiraan jumlah untuk pekerjaan bangunan atau renovasi Anda.",
              },
              {
                icon: MessageCircle,
                num: "02",
                title: "Chat NJA",
                text: `Kirimkan daftar kebutuhan Anda ke WhatsApp ${site.whatsappDisplay}. Foto barang atau instalasi lama sangat membantu.`,
              },
              {
                icon: ListChecks,
                num: "03",
                title: "Konfirmasi harga dan stok",
                text: "Tim NJA membalas dengan informasi harga dan ketersediaan barang untuk kebutuhan Anda.",
              },
            ].map((step, i) => (
              <Reveal key={step.num} delay={i * 0.08}>
                <div className="h-full rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
                  <div className="flex items-center justify-between">
                    <step.icon className="h-7 w-7 text-brand" aria-hidden="true" />
                    <span className="text-3xl font-extrabold text-slate-200" aria-hidden="true">
                      {step.num}
                    </span>
                  </div>
                  <h3 className="mt-4 text-lg font-semibold text-navy">{step.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-slate-600">{step.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Tentang singkat */}
      <section className="mx-auto max-w-[1200px] px-4 py-10 sm:px-6 lg:py-16" aria-labelledby="tentang-heading">
        <div className="grid items-start gap-6 md:grid-cols-2">
          <Reveal>
            <p className="text-sm font-semibold text-brand">Tentang</p>
            <h2 id="tentang-heading" className="mt-2 text-2xl font-bold text-navy sm:text-4xl">
              {site.name}
            </h2>
            <p className="mt-4 text-base leading-relaxed text-slate-600">
              {site.name} adalah toko bangunan yang berlokasi di {site.address},{" "}
              {site.region}. Kami melayani pemilik rumah, tukang, dan kontraktor
              yang membutuhkan bahan bangunan untuk proyek pembangunan maupun
              renovasi di Bajawa dan sekitarnya.
            </p>
            <Link
              to="/tentang"
              data-testid="home-about-link"
              className="group mt-5 inline-flex items-center gap-1 font-semibold text-brand hover:underline"
            >
              Baca profil lengkap
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" aria-hidden="true" />
            </Link>
          </Reveal>
          <Reveal delay={0.12}>
            <div className="rounded-2xl bg-navy p-7 text-white shadow-lg">
              <h3 className="flex items-center gap-2 text-lg font-semibold">
                <MapPin className="h-5 w-5 text-brand" aria-hidden="true" />
                Informasi kontak
              </h3>
              <dl className="mt-4 space-y-4 text-sm">
                <div>
                  <dt className="font-medium text-white/60">Alamat</dt>
                  <dd className="mt-1 text-white/90">{site.address}, {site.region}</dd>
                </div>
                <div>
                  <dt className="font-medium text-white/60">WhatsApp</dt>
                  <dd className="mt-1">
                    <a
                      href={waLink(waGeneralMessage)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="font-semibold text-white hover:underline"
                    >
                      {site.whatsappDisplay}
                    </a>
                  </dd>
                </div>
              </dl>
              <div className="mt-6 flex flex-wrap items-center gap-3">
                <Link
                  to="/kontak"
                  data-testid="home-contact-link"
                  className="inline-block rounded-full bg-brand px-5 py-2.5 text-sm font-semibold text-white transition-colors duration-300 hover:bg-brand-dark"
                >
                  Lihat Halaman Kontak
                </Link>
                {site.googleMapsUrl && (
                  <a
                    href={site.googleMapsUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="home-directions-link"
                    className="inline-block rounded-full border border-white/30 px-5 py-2.5 text-sm font-semibold text-white transition-colors duration-300 hover:bg-white/10"
                  >
                    Petunjuk Arah
                  </a>
                )}
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Peta lokasi */}
      {site.googleMapsEmbedUrl && (
        <section className="mx-auto max-w-[1200px] px-4 pb-10 sm:px-6 lg:pb-16" aria-label="Peta lokasi toko">
          <Reveal>
            <div className="overflow-hidden rounded-2xl border border-slate-200 shadow-sm">
              <iframe
                src={site.googleMapsEmbedUrl}
                title="Peta lokasi Toko Bangunan Nuansa Jaya Abadi di Bajawa"
                data-testid="home-map-embed"
                loading="lazy"
                allowFullScreen
                referrerPolicy="no-referrer-when-downgrade"
                className="h-72 w-full border-0"
              />
            </div>
          </Reveal>
        </section>
      )}

      {/* Media sosial */}
      <section className="mx-auto max-w-[1200px] px-4 pb-10 sm:px-6 lg:pb-16" aria-labelledby="sosial-heading">
        <Reveal>
          <p className="text-sm font-semibold text-brand">Media sosial</p>
          <h2 id="sosial-heading" className="mt-2 text-2xl font-bold text-navy sm:text-4xl">
            Ikuti Nuansa Jaya Abadi
          </h2>
          <p className="mt-3 max-w-2xl text-base leading-relaxed text-slate-600">
            Temukan informasi produk, inspirasi kebutuhan bangunan, dan kabar
            terbaru NJA melalui media sosial kami.
          </p>
        </Reveal>
        <div className="mt-8 grid gap-4 sm:grid-cols-3">
          {site.socials.map((s, i) => (
            <Reveal key={s.label} delay={i * 0.06}>
              <a
                href={s.url}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={`${s.label} NJA`}
                data-testid={`home-social-${s.label.toLowerCase()}`}
                className="group flex h-full items-center gap-4 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm transition-shadow duration-300 hover:shadow-md"
              >
                <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-brand/10 text-brand transition-transform duration-300 group-hover:scale-110">
                  <s.icon className="h-6 w-6" aria-hidden="true" />
                </span>
                <span className="font-semibold text-navy">{s.label} NJA</span>
                <ArrowUpRight className="ml-auto h-5 w-5 shrink-0 text-slate-300 transition-colors duration-300 group-hover:text-brand" aria-hidden="true" />
              </a>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Galeri foto toko — tampil setelah galleryPhotos diisi di data/store.js */}
      {site.galleryPhotos.length > 0 && (
        <section className="mx-auto max-w-[1200px] px-4 pb-10 sm:px-6 lg:pb-16" aria-labelledby="galeri-heading">
          <Reveal>
            <p className="text-sm font-semibold text-brand">Galeri</p>
            <h2 id="galeri-heading" className="mt-2 text-2xl font-bold text-navy sm:text-4xl">
              Kenali toko kami
            </h2>
          </Reveal>
          <div className="mt-8 grid grid-cols-2 gap-4 lg:grid-cols-4">
            {site.galleryPhotos.map((p, i) => (
              <Reveal key={p.src} delay={i * 0.06}>
                <img
                  src={p.src}
                  alt={p.alt}
                  width="600"
                  height="450"
                  loading="lazy"
                  className="aspect-[4/3] w-full rounded-2xl object-cover shadow-sm"
                />
              </Reveal>
            ))}
          </div>
        </section>
      )}

      {/* Informasi belanja */}
      <section className="mx-auto max-w-[1200px] px-4 pb-10 sm:px-6 lg:pb-16" aria-labelledby="info-belanja-heading">
        <Reveal>
          <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm sm:p-8">
            <p className="text-sm font-semibold text-brand">Informasi belanja</p>
            <h2 id="info-belanja-heading" className="mt-2 text-2xl font-bold text-navy sm:text-4xl">
              Pengantaran, pembayaran, dan proyek
            </h2>
            {Object.values(site.shoppingInfo).some(Boolean) ? (
              <dl className="mt-5 grid gap-4 text-sm sm:grid-cols-2">
                {Object.entries(INFO_LABELS)
                  .filter(([key]) => site.shoppingInfo[key])
                  .map(([key, label]) => (
                    <div key={key}>
                      <dt className="font-semibold text-navy">{label}</dt>
                      <dd className="mt-1 leading-relaxed text-slate-600">{site.shoppingInfo[key]}</dd>
                    </div>
                  ))}
              </dl>
            ) : (
              <p className="mt-4 max-w-2xl text-base leading-relaxed text-slate-600">
                Untuk informasi pengantaran, pembayaran, dan kebutuhan proyek,
                silakan hubungi tim NJA.
              </p>
            )}
            <a
              href={waLink(waInfoMessage)}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="shopping-info-wa-button"
              className="mt-6 inline-flex items-center gap-2 rounded-full bg-wa px-6 py-3 text-sm font-semibold text-white transition-colors duration-300 hover:bg-wa-dark"
            >
              <MessageCircle className="h-5 w-5" aria-hidden="true" />
              Tanya Info Belanja via WhatsApp
            </a>
          </div>
        </Reveal>
      </section>

      {/* Merek yang tersedia — tampil setelah site.brands diisi di data/store.js */}
      {site.brands.length > 0 && (
        <section className="mx-auto max-w-[1200px] px-4 pb-10 sm:px-6 lg:pb-16" aria-labelledby="merek-heading">
          <Reveal>
            <p className="text-sm font-semibold text-brand">Merek</p>
            <h2 id="merek-heading" className="mt-2 text-2xl font-bold text-navy sm:text-4xl">
              Merek yang tersedia
            </h2>
          </Reveal>
          <ul className="mt-6 flex flex-wrap gap-3">
            {site.brands.map((b) => (
              <li
                key={b}
                className="rounded-full border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-navy shadow-sm"
              >
                {b}
              </li>
            ))}
          </ul>
        </section>
      )}

      {/* FAQ */}
      <section className="bg-slate-50" aria-labelledby="faq-heading">
        <div className="mx-auto max-w-3xl px-4 py-10 sm:px-6 lg:py-16">
          <Reveal>
            <p className="text-sm font-semibold text-brand">FAQ</p>
            <h2 id="faq-heading" className="mt-2 text-2xl font-bold text-navy sm:text-4xl">
              Sering ditanyakan
            </h2>
          </Reveal>
          <div className="mt-8 space-y-3">
            {faq.map((item, i) => (
              <Reveal key={item.q} delay={i * 0.05}>
                <details
                  data-testid={`faq-item-${i}`}
                  className="group rounded-2xl border border-slate-200 bg-white p-5 shadow-sm"
                >
                  <summary className="cursor-pointer list-none text-base font-semibold text-navy marker:hidden">
                    <span className="flex items-center justify-between gap-3">
                      {item.q}
                      <ArrowRight className="h-4 w-4 shrink-0 rotate-90 text-brand transition-transform duration-300 group-open:-rotate-90" aria-hidden="true" />
                    </span>
                  </summary>
                  <p className="mt-3 text-sm leading-relaxed text-slate-600">{item.a}</p>
                </details>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CTA akhir — kartu biru tua, aksen merah, tombol hijau */}
      <section className="mx-auto max-w-[1200px] px-4 py-10 sm:px-6 lg:py-16">
        <Reveal>
          <div className="rounded-2xl bg-navy px-6 py-12 text-center text-white shadow-lg sm:px-10">
            <p className="text-sm font-semibold text-[#FF7A6B]">Siap membantu</p>
            <h2 className="mt-2 text-2xl font-bold sm:text-4xl">
              Butuh bahan bangunan di Bajawa?
            </h2>
            <p className="mx-auto mt-3 max-w-xl text-base leading-relaxed text-white/80">
              Kirim daftar kebutuhan Anda — tim NJA bantu cek harga dan
              ketersediaannya.
            </p>
            <a
              href={waLink(waGeneralMessage)}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="cta-whatsapp-button"
              className="mt-7 inline-flex items-center gap-2 rounded-full bg-wa px-8 py-3.5 text-base font-semibold text-white transition-colors duration-300 hover:bg-wa-dark"
            >
              <MessageCircle className="h-5 w-5" aria-hidden="true" />
              Tanya Harga via WhatsApp
            </a>
          </div>
        </Reveal>
      </section>
    </>
  );
}
